<template>
  <div class="ai-actions-content">
    <template v-if="hasAiAddon">
      <div class="ai-actions-content__playback">
        <div class="ai-actions-content__section-label">AI Playback</div>
        <div class="ai-actions-content__playback-chips">
          <div class="ai-actions-content__chip-group">
            <label class="ai-actions-content__chip" :class="{ 'ai-actions-content__chip--disabled': !hasFillerWords }">
              <input
                type="checkbox"
                class="ai-actions-content__chip-checkbox"
                :checked="skipFillerWords"
                :disabled="!hasFillerWords"
                data-testid="ai-playback-skip-filler-words"
                @change="toggleSkipFillerWords"
              />
              <span>Skip Filler Words</span>
            </label>
            <span class="ai-actions-content__chip-caption" :class="{ 'ai-actions-content__chip-caption--muted': !hasFillerWords }">
              {{ hasFillerWords ? `${fillerWordsCount} filler word${fillerWordsCount === 1 ? '' : 's'} will be skipped` : 'No filler words detected' }}
            </span>
          </div>
          <div class="ai-actions-content__chip-group">
            <label class="ai-actions-content__chip" :class="{ 'ai-actions-content__chip--disabled': !hasSilences }">
              <input
                type="checkbox"
                class="ai-actions-content__chip-checkbox"
                :checked="skipSilences"
                :disabled="!hasSilences"
                data-testid="ai-playback-skip-silences"
                @change="toggleSkipSilences"
              />
              <span>Skip Silences</span>
            </label>
            <span class="ai-actions-content__chip-caption" :class="{ 'ai-actions-content__chip-caption--muted': !hasSilences }">
              {{
                hasSilences
                  ? `${silencesCount} silence${silencesCount === 1 ? '' : 's'} (${silencesTotalSeconds.toFixed(1)}s) will be skipped`
                  : 'No silences detected'
              }}
            </span>
          </div>
        </div>
        <div v-if="effectiveDurationLabel" class="ai-actions-content__effective-duration" data-testid="ai-playback-effective-duration">
          {{ effectiveDurationLabel }}
        </div>
      </div>
      <McpContextNotice class="ai-actions-content__mcp-notice" placement="ai_actions" />
      <div class="ai-actions-content__divider" />
    </template>
    <template v-if="canRedact">
      <div class="ai-actions-content__section-label" :class="{ 'ai-actions-content__section-label--actions': hasAiAddon }">Privacy</div>
      <div v-if="!hasSeenRedactHint" class="ai-actions-content__redact-hint" data-testid="viewer-redact-hint" role="status">
        <p>New: hide emails, names, and other sensitive text on screen in your video.</p>
        <button type="button" data-testid="viewer-redact-hint-dismiss" @click="dismissRedactHint">Got it</button>
      </div>
      <button type="button" class="ai-actions-content__redact" :disabled="isRedactDisabled" data-testid="ai-actions-redact" @click="onRedactClick">
        <span class="ai-actions-content__redact-icon">
          <Icon name="general.shieldEmail" size="base" />
        </span>
        <span>{{ isRedactBusy ? 'Redacting sensitive text…' : 'Redact sensitive text' }}</span>
      </button>
      <span v-if="redactionResultLabel" class="ai-actions-content__chip-caption" data-testid="ai-actions-redact-result">{{ redactionResultLabel }}</span>
      <div class="ai-actions-content__divider" :class="{ 'ai-actions-content__divider--spaced': !hasAiAddon }" />
    </template>
    <template v-if="hasAiAddon">
      <div class="ai-actions-content__section-label ai-actions-content__section-label--actions">Smart Actions</div>
      <SmartActionsList :exclude-keys="smartActionsExcludeKeys" :show-title="false" />
    </template>
    <SmartActionsUpsell v-else />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useStorage } from '@vueuse/core';
import { Icon } from '@/storybook';

import SmartActionsList from './SmartActionsList.vue';
import SmartActionsUpsell from './SmartActionsUpsell.vue';
import McpContextNotice from './McpContextNotice.vue';

import { useVideoAi } from '@/modules/viewer/composables/useVideoAi';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useAiPlayback } from '@/modules/viewer/composables/useAiPlayback';
import { useRedaction } from '@/modules/viewer/composables/useRedaction';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';

const { hasAiAddon } = useVideoAi();
const { isMeeting } = storeToRefs(useViewerItemStore());

const {
  hasFillerWords,
  hasSilences,
  fillerWordsCount,
  silencesCount,
  silencesTotalSeconds,
  effectiveDurationLabel,
  skipFillerWords,
  skipSilences,
  toggleSkipFillerWords,
  toggleSkipSilences,
} = useAiPlayback();

const { canRedact, isRedactBusy, isRedactDisabled, redactionResultLabel } = useRedaction();
const { redactAction } = useItemActions();

// Global, not per item: once seen, the hint stays gone on every video.
const hasSeenRedactHint = useStorage('viewer-redact-hint-ai-actions-seen', false);
const dismissRedactHint = () => {
  hasSeenRedactHint.value = true;
};

const onRedactClick = () => {
  dismissRedactHint();
  redactAction();
};

// meeting_summary moves out of the list for meeting items, where the Meeting
// Details tab (attendees/organizer, a different data source) already owns
// that label.
const smartActionsExcludeKeys = computed(() => (isMeeting.value ? ['meeting_summary'] : []));
</script>

<style scoped>
.ai-actions-content__section-label {
  @apply text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2;
}

.ai-actions-content__section-label--actions {
  @apply mt-4;
}

.ai-actions-content__mcp-notice {
  @apply mt-4;
}

/* Plain grey rule between AI Playback and Smart Actions — replaces the
   gradient divider SmartActionsList carries for its own standalone use
   (hidden below when nested here), which read as an "AI" accent out of
   place next to a neutral checkbox section. */
.ai-actions-content__divider {
  @apply border-t border-zinc-200 dark:border-zinc-700 mt-4;
}

.ai-actions-content__divider--spaced {
  @apply mb-4;
}

.ai-actions-content__playback-chips {
  @apply flex flex-wrap gap-4;
}

.ai-actions-content__chip-group {
  @apply flex flex-col gap-1;
}

.ai-actions-content__chip {
  @apply inline-flex items-center gap-2 text-sm font-semibold text-zinc-700 dark:text-zinc-200 cursor-pointer;
}

.ai-actions-content__chip--disabled {
  @apply opacity-50 cursor-not-allowed;
}

.ai-actions-content__chip-checkbox {
  @apply w-4 h-4 rounded cursor-pointer;
  accent-color: #9557af;
}

/* Same light/dark AI-purple pairing SmartActionsList uses throughout this
   component family (e.g. .smart-action-item__icon) — accent-color has no
   Tailwind utility, so this stays a bare hex/media-query pair like the rest
   of them rather than a one-off CSS custom property. */
.dark .ai-actions-content__chip-checkbox {
  accent-color: #e0adff;
}

.ai-actions-content__chip-checkbox:disabled {
  @apply cursor-not-allowed;
}

.ai-actions-content__redact {
  @apply flex items-center gap-3 w-full py-2 rounded-lg text-left text-sm font-medium text-zinc-700 dark:text-zinc-200 transition-colors cursor-pointer;
}

.ai-actions-content__redact:hover:not(:disabled) {
  @apply bg-zinc-50 dark:bg-zinc-700/50;
}

.ai-actions-content__redact:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.ai-actions-content__redact-icon {
  @apply w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300;
}

.ai-actions-content__redact-hint {
  @apply relative flex flex-col gap-2 mb-3 p-3 rounded-lg shadow-lg text-sm leading-snug;
  @apply bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900;
}

/* Caret pointing down at the Redact row. */
.ai-actions-content__redact-hint::before {
  content: '';
  @apply absolute -bottom-1.5 left-4 w-3 h-3 rotate-45 bg-zinc-900 dark:bg-zinc-100;
}

.ai-actions-content__redact-hint button {
  @apply self-end text-xs font-bold uppercase tracking-wide text-blue-300 dark:text-blue-700 cursor-pointer hover:underline;
}

.ai-actions-content__chip-caption {
  @apply text-xs text-zinc-600 dark:text-zinc-300;
}

.ai-actions-content__chip-caption--muted {
  @apply text-zinc-400 dark:text-zinc-500;
}

/* The player's own timeline still shows the file's real (unshortened)
   duration — skipping happens by seeking during playback, not by editing
   the file — so this is the only place the shortened runtime shows up. */
.ai-actions-content__effective-duration {
  @apply text-xs font-medium text-zinc-600 dark:text-zinc-300 mt-3;
}

/* SmartActionsList/SmartActionsUpsell carry their own card chrome for
   standalone use elsewhere; flatten it here regardless of which parent
   (the tabbed video-mode panel, or the summary-mode sidebar) embeds this
   component, since both provide their own single outer card. */
.ai-actions-content :deep(.smart-actions-list-wrapper__header),
.ai-actions-content :deep(.smart-actions-list-wrapper__limit),
.ai-actions-content :deep(.smart-actions-list-wrapper__content) {
  @apply px-0;
}

.ai-actions-content :deep(.smart-actions-list-wrapper__divider) {
  display: none;
}

.ai-actions-content :deep(.smart-actions-upsell) {
  @apply border-0 rounded-none bg-transparent p-0;
}
</style>
